import { useState } from "react";
import { Box, Button, Typography, Avatar, Stack, IconButton } from "@mui/material";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { CallerInfo } from "../incoming-call/IncomingCallNotification";
import { formatUSPhone } from "../../../../utils";
import { colors, alphaVar } from "../../../ui/colors";
import { TransferPanel } from "./transfer/TransferPanel";
import { PauseRounded, PlayArrowRounded } from "@mui/icons-material";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";

interface Props {
  callerInfo: CallerInfo;
  companyId: string;
  callDuration: number;
  isMuted: boolean;
  hangupCall: () => void;
  toggleMute: () => void;
  transferCall: (agentUid: string) => void;
  /** Full phone number used when opening the add-contact menu. */
  contactPhoneNumber?: string | null;
  onQuickSave?: (phoneNumber: string, anchorEl: HTMLElement) => void;
  isInternal?: boolean;
}

export const InCallNotification = ({
  callerInfo,
  callDuration,
  isMuted,
  hangupCall,
  toggleMute,
  contactPhoneNumber,
  onQuickSave,
  isInternal = false,
}: Props) => {
  const { theme } = useContactCenterTheme();
  const { displayName, dialCode, phoneNumber, callerType, receivingCompanyName } = callerInfo;

  const [showTransfer, setShowTransfer] = useState(false);
  const { isOnHold, toggleHold } = useVirtualAssistant();

  const isUnknownContact =
    !isInternal && (!displayName || displayName === "Unknown") && !!contactPhoneNumber && !!onQuickSave;

  const canTransfer = !isInternal;

  const callerInitials = displayName?.at(0)?.toUpperCase() || "?";
  const callerPhoneDisplay = dialCode ? `${dialCode} ${formatUSPhone(phoneNumber)}` : phoneNumber;

  const callerLabel = callerType === "client" ? "Client" : callerType === "workforce" ? "Workforce" : null;

  const formatDuration = (sec: number) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleToggleTransfer = () => setShowTransfer((prev) => !prev);

  return (
    <Box sx={{ textAlign: "center", mb: 0.5 }}>
      <Avatar
        sx={{
          width: 52,
          height: 52,
          mx: "auto",
          backgroundColor: theme.shell.accent,
          color: "#fff",
          fontSize: "1.2rem",
          fontWeight: 700,
          boxShadow: `0 2px 8px ${alphaVar(theme.shell.accent, 0.3)}`,
        }}
      >
        {callerInitials}
      </Avatar>

      <Box
        sx={{
          mt: 1,
          mb: 0.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            color: theme.shell.textPrimary,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {displayName || "Unknown"}
        </Typography>
        {isUnknownContact && (
          <IconButton
            size="small"
            aria-label="Add contact"
            onClick={(e) => {
              e.stopPropagation();
              onQuickSave?.(contactPhoneNumber!, e.currentTarget);
            }}
            sx={{
              p: 0.35,
              color: theme.shell.textSecondary,
              "&:hover": {
                color: theme.shell.accent,
                bgcolor: alphaVar(theme.shell.accent, 0.08),
              },
            }}
          >
            <PersonAddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: "0.75rem",
          color: theme.shell.textSecondary,
          mt: 0.25,
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}
      >
        {callerPhoneDisplay}
      </Typography>

      {callerLabel && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: theme.shell.textSecondary,
            lineHeight: 1.35,
          }}
        >
          {callerLabel}
        </Typography>
      )}

      {receivingCompanyName && (
        <Typography
          sx={{
            mt: 0.25,
            fontSize: "0.75rem",
            color: theme.shell.textSecondary,
            fontStyle: "italic",
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          For: {receivingCompanyName}
        </Typography>
      )}

      <Typography
        sx={{
          mt: 0.75,
          fontSize: "0.9rem",
          fontWeight: 600,
          color: theme.shell.textSecondary,
        }}
      >
        {formatDuration(callDuration)}
      </Typography>

      {isOnHold && (
        <Box
          sx={{
            mt: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.76rem",
              fontWeight: 600,
              color: theme.shell.textPrimary,
            }}
          >
            Caller on hold
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              mt: 0.2,
              color: theme.shell.textSecondary,
            }}
          >
            Please wait while we keep the caller on the line.
          </Typography>
        </Box>
      )}

      <Stack spacing={1} sx={{ mt: 1.25 }}>
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            onClick={toggleMute}
            startIcon={isMuted ? <MicOffIcon sx={{ fontSize: "0.95rem" }} /> : <MicIcon sx={{ fontSize: "0.95rem" }} />}
            sx={{
              minWidth: 0,
              minHeight: 40,
              color: theme.shell.textPrimary,
              fontWeight: 600,
              fontSize: "0.85rem",
              px: 1.25,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: alphaVar(theme.shell.accent, 0.08),
                color: theme.shell.textPrimary,
                boxShadow: "none",
              },
            }}
          >
            {isMuted ? "Unmute" : "Mute"}
          </Button>

          <Button
            fullWidth
            onClick={toggleHold}
            startIcon={isOnHold ? <PlayArrowRounded sx={{ fontSize: "1rem" }} /> : <PauseRounded sx={{ fontSize: "1rem" }} />}
            sx={{
              minWidth: 0,
              minHeight: 40,
              color: isOnHold ? theme.shell.accent : theme.shell.textPrimary,
              fontWeight: 600,
              fontSize: "0.85rem",
              px: 1.25,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              whiteSpace: "nowrap",
              backgroundColor: isOnHold ? alphaVar(theme.shell.accent, 0.1) : "transparent",
              border: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: alphaVar(theme.shell.accent, 0.1),
                boxShadow: "none",
              },
              "& .MuiButton-startIcon": {
                marginRight: "6px",
              },
            }}
          >
            {isOnHold ? "Resume" : "Hold"}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1}>
          {canTransfer && (
            <Button
              fullWidth
              onClick={handleToggleTransfer}
              startIcon={<PhoneForwardedIcon sx={{ fontSize: "0.95rem" }} />}
              sx={{
                minWidth: 0,
                minHeight: 40,
                color: theme.shell.accent,
                fontWeight: 600,
                fontSize: "0.85rem",
                px: 1.25,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                whiteSpace: "nowrap",
                backgroundColor: showTransfer ? alphaVar(theme.shell.accent, 0.1) : "transparent",
                border: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: alphaVar(theme.shell.accent, 0.1),
                  boxShadow: "none",
                },
              }}
            >
              Transfer
            </Button>
          )}

          <Button
            fullWidth
            onClick={hangupCall}
            startIcon={<PhoneDisabledIcon sx={{ fontSize: "0.95rem" }} />}
            sx={{
              minWidth: 0,
              minHeight: 40,
              color: colors.danger.base,
              fontWeight: 700,
              fontSize: "0.85rem",
              px: 1.25,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: alphaVar(colors.danger.base, 0.07),
                color: colors.danger[700],
                boxShadow: "none",
              },
            }}
          >
            Hang Up
          </Button>
        </Stack>
      </Stack>

      {canTransfer && showTransfer && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: `1px solid ${alphaVar(theme.shell.textSecondary, 0.18)}`,
            textAlign: "left",
          }}
        >
          <TransferPanel />
        </Box>
      )}
    </Box>
  );
};
